import { useEffect, useState } from "react";

import { IColumn, ITableRowData } from "../../../components/data-table/data-table";

import "./process-type-stage.scss";
import { Button } from "react-bootstrap";
import { AiOutlineEdit } from "react-icons/ai";
import { MdOutlineKeyboardArrowDown, MdOutlineKeyboardArrowRight } from "react-icons/md";
import { RxCross2 } from "react-icons/rx";

import { IEnumerationOption } from "../../../../shared/definitions/datatypes";
import { processTypeStageActionTypesOptions, processTypeStageToPerformOptions } from "../../../../shared/utils/static-values";
import { createUUID } from "../../../../shared/utils/utils";
import ControlledTable from "../../../components/controlled-table/controlled-table";
import SearchInput from "../../../components/search-input/search-input";
import { noop } from "../../../utils";
import UserGroupSelectPopup from "./user-group-select-popup";
import ActionPopup from "./action-popup";
import Checkbox from "@/manager/components/checkbox/checkbox";

interface IProcessTypeStageProps {
    stages: ITableRowData[];
    processTypeStage: ITableRowData;
    onStageDataChange: (stage: ITableRowData) => void;
    onStageDelete: (stageId: string) => void;
}

const actionColumns: IColumn[] = [
  {
    key: "name",
    displayName: "Name",
    sortable: false,
    dataType: {
      type: "text",
    },
  },
  {
    key: "action_type",
    displayName: "Action type",
    sortable: false,
    dataType: {
      type: "enumeration",
      values: processTypeStageActionTypesOptions
    }
  },
  {
    key: "to_perform",
    displayName: "To perform",
    sortable: false,
    dataType: {
      type: "enumeration",
      values: processTypeStageToPerformOptions,
    }
  }
];

const ProcessTypeStage = ({ stages, processTypeStage, onStageDataChange, onStageDelete }: IProcessTypeStageProps) => {
  const [isCollapsed, setIsCollapsed] = useState(processTypeStage.domain_stage_id !== "");
  const [showUserGroupPopup, setShowUserGroupPopup] = useState(false);
  const [showActionPopup, setShowActionPopup] = useState(false);
  const [stage, setStage] = useState<ITableRowData>({ id: "" });
  const [selectedAction, setSelectedAction] = useState<ITableRowData | undefined>(undefined);

  useEffect(() => {
    setStage(processTypeStage);
  }, [processTypeStage]);

  const handleOnSelectedStageChange = (selectedOption: IEnumerationOption | undefined) => {
    if (!selectedOption) {
      return;
    }
    const newStage = { ...stage, domain_stage_id: selectedOption.id, domain_stage_name: selectedOption.name };
    setStage(newStage);
    onStageDataChange(newStage);
  };

  const handleUserGroupSelected = (userGroups: ITableRowData[]) => {
    setShowUserGroupPopup(false);

    const existingGroups = stage.process_type_stage_user_groups?.filter((userGroup: ITableRowData) => userGroups.find((ug: ITableRowData) => ug.id === userGroup.user_group_id));
    const newGroups = userGroups.filter((userGroup: ITableRowData) => (
      !stage.process_type_stage_user_groups?.find((ug: ITableRowData) => (
        ug.user_group_id === userGroup.id
      ))
    ))
      .map((userGroup: ITableRowData) => ({ id: createUUID(), user_group_id: userGroup.id, user_group_name: userGroup.name }));

    const newStage = { ...stage, process_type_stage_user_groups: [...existingGroups, ...newGroups] };
    setStage(newStage);
    onStageDataChange(newStage);
  };

  const handleStageDeleteClick = (clickEvent: React.MouseEvent) => {
    clickEvent.stopPropagation();
    onStageDelete(stage.id);
  };

  const handleActionRowClick = (id: string) => {
    setShowActionPopup(true);
    const selectedAction = stage.process_type_stage_actions.find((action: ITableRowData) => action.id === id);
    setSelectedAction(selectedAction);
  };

  const handleActionPopupClosed = (popupAction: ITableRowData | undefined) => {
    setShowActionPopup(false);
    setSelectedAction(undefined);
    if (!popupAction) {
      return;
    }

    const existingAction = stage.process_type_stage_actions.find((action: ITableRowData) => action.selected_action_id === popupAction.selected_action_id);
    if (existingAction) {
      const newStage = { ...stage, process_type_stage_actions: stage.process_type_stage_actions.map((action: ITableRowData) => action.selected_action_id === popupAction.selected_action_id ? popupAction : action) };
      setStage(newStage);
      onStageDataChange(newStage);
      return;
    } else {
      const newStage = { ...stage, process_type_stage_actions: [...stage.process_type_stage_actions, popupAction] };
      setStage(newStage);
      onStageDataChange(newStage);
    }
  };

  const handleActionRowDelete = (ids: string[]) => {
    const newStage = { ...stage, process_type_stage_actions: stage.process_type_stage_actions.filter((action: ITableRowData) => !ids.includes(action.id)) };
    setStage(newStage);
    onStageDataChange(newStage);
  };

  return <div className="processTypeStage">
    <UserGroupSelectPopup
      show={showUserGroupPopup}
      handlePopupClosed={() => setShowUserGroupPopup(false)}
      handleUserGroupSelected={handleUserGroupSelected}
      preSelectedUserGroups={stage.process_type_stage_user_groups?.map((userGroup: ITableRowData) => userGroup.user_group_id)}
    />
    <ActionPopup
      show={showActionPopup}
      handlePopupClosed={handleActionPopupClosed}
      action={selectedAction}
    />
    <div className="processTypeStage-header" onClick={() => setIsCollapsed(!isCollapsed)}>
      <div className="processTypeStage-name">
        {stage.domain_stage_name} {stage.mandatory && <span>(Mandatory)</span>}
      </div>
      <div className="processTypeStage-actions">
        <div className="processTypeStage-action">
          {isCollapsed ? <MdOutlineKeyboardArrowRight /> : <MdOutlineKeyboardArrowDown />}
        </div>
        <div className="processTypeStage-action" onClick={handleStageDeleteClick}>
          <RxCross2 />
        </div>
      </div>
    </div>
    {!isCollapsed && (
      <div className="processTypeStage-content">
        <Checkbox label="Mandatory" checked={!!stage.mandatory} onClick={() => {
          const newStage = { ...stage, mandatory: !stage.mandatory };
          setStage(newStage);
          onStageDataChange(newStage);
        }} />
        <SearchInput
          label="Stage"
          handleOnChange={handleOnSelectedStageChange}
          data={stages?.map((stage: ITableRowData) => ({ id: stage.id, name: stage.name }))}
          inputKey="stage"
          required={true}
          selectedOption={stage.domain_stage_id}
        />
        <div className="processTypeStage-userGroupSelect">
          <div className="processTypeStage-userGroupSelectHeader">
            <h4><b>User Groups</b> </h4>
            <Button
              className="processTypeStage-userGroupSelectButton"
              name="assignUserGroup"
              onClick={() => setShowUserGroupPopup(true)}
              variant="primary"
            >
              <AiOutlineEdit />
            </Button>
          </div>
          <div className="processTypeStage-userGroups">
            {stage.process_type_stage_user_groups?.map((userGroup: ITableRowData) => (
              <div key={userGroup.id} className="processTypeStage-userGroup">
                                - {userGroup.user_group_name}
              </div>
            ))}
          </div>
        </div>
        <div className="processTypeStage-actionsSelect">
          <ControlledTable
            title="Actions"
            data={stage.process_type_stage_actions || []}
            selectionMode="multi"
            columns={actionColumns}
            onSorted={noop}
            onNewClicked={() => {
              setSelectedAction(undefined);
              setShowActionPopup(true);
            }}
            onRowClick={handleActionRowClick}
            onDeleted={handleActionRowDelete}
            onScroll={noop}
          />
        </div>
      </div>
    )}
  </div>;
};

export default ProcessTypeStage;
